import { getHexTileMapKey } from '../../hexmap/model/keys';
import { milliCost, toMilli } from '../fixed';
import { buildingLevel } from '../levels';
import {
  isMilitary,
  nearestFree,
  type Occupancy,
  occupancyOf,
  reachOf,
  storeWithRoom,
} from '../occupancy';
import { mintUnitId } from '../state';
import { canAfford, holdsStock, spend } from '../stock';
import { territoryOwners } from '../territory';
import { MAX_TRAINING_QUEUE, queuedUnits, trainingBuilding } from '../training';
import type {
  Building,
  Cell,
  EntityId,
  PlayerId,
  SimContext,
  SimState,
  Unit,
  UnitType,
} from '../types';

/** The type whose cap `unitType` counts against: its `capAs`, else itself. */
function capType(rules: SimContext['rules'], unitType: UnitType): UnitType {
  return rules.units[unitType]?.capAs ?? unitType;
}

/**
 * How many units of `unitType` `playerId` may have: summed over their
 * completed cities, each allowing the type's `perCity` plus `perCityLevel`
 * per level above 1 (Infinity when the type has no cap). A type with
 * `capAs` shares that type's cap.
 */
function unitCap(
  state: SimState,
  rules: SimContext['rules'],
  playerId: PlayerId,
  type: UnitType
): number {
  const unitType = capType(rules, type);
  const def = rules.units[unitType];
  const perCity = def?.perCity;
  if (perCity === undefined) return Number.POSITIVE_INFINITY;
  const perLevel = def?.perCityLevel ?? 0;
  // A type no building trains (a spy, only promoted into) counts every
  // completed city.
  const trained = Object.values(rules.buildings).some((d) => d.spawns?.includes(unitType));
  let cap = 0;
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId || b.buildProgress < 1) continue;
    const def = rules.buildings[b.type];
    if (trained ? def?.spawns?.includes(unitType) : holdsStock(rules, b)) {
      cap += perCity + perLevel * (buildingLevel(b) - 1);
    }
  }
  return cap;
}

/** Units of `unitType` `playerId` currently has. */
function unitCount(state: SimState, playerId: PlayerId, unitType: UnitType): number {
  let n = 0;
  for (const u of Object.values(state.units)) {
    if (u.owner === playerId && u.type === unitType) n++;
  }
  return n;
}

/**
 * Units counting against `unitType`'s cap (`unitCap`) `playerId` currently
 * has: those of its cap type (see `capType`) and of every type sharing it
 * (labourers count as workers), queued ones (training or waiting) included.
 */
function capCount(
  state: SimState,
  rules: SimContext['rules'],
  playerId: PlayerId,
  unitType: UnitType
): number {
  const cap = capType(rules, unitType);
  let n = 0;
  for (const u of Object.values(state.units)) {
    if (u.owner === playerId && capType(rules, u.type) === cap) n++;
  }
  for (const t of queuedUnits(state, playerId)) if (capType(rules, t.unitType) === cap) n++;
  return n;
}

/**
 * What training a `unitType` costs `playerId` now: its `cost`, plus
 * `costPerCity` of it for each city beyond the first. Cities are the
 * player's buildings that train this type, unfinished ones included, plus
 * their living units of this type (settlers on their way to found one), so
 * queued settlers can't be bought at the old price. Amounts are rounded up.
 */
function unitCost(
  state: SimState,
  rules: SimContext['rules'],
  playerId: PlayerId,
  unitType: UnitType
): Record<string, number> {
  const def = rules.units[unitType];
  if (!def) return {};
  const perCity = def.costPerCity ?? 0;
  if (perCity === 0) return def.cost;
  let cities = unitCount(state, playerId, unitType);
  for (const b of Object.values(state.buildings)) {
    if (b.owner === playerId && rules.buildings[b.type]?.spawns?.includes(unitType)) cities++;
  }
  const scale = 1 + perCity * Math.max(0, cities - 1);
  const cost: Record<string, number> = {};
  for (const [res, amount] of Object.entries(def.cost)) cost[res] = Math.ceil(amount * scale);
  return cost;
}

/**
 * Whether city `cityId` meets `unitType`'s `requires`: a completed building of
 * that type and level (or higher), its owner's, in this city's territory.
 * True when the type has no requirement.
 */
function unitUnlocked(
  state: SimState,
  rules: SimContext['rules'],
  cityId: EntityId,
  unitType: UnitType
): boolean {
  const req = rules.units[unitType]?.requires;
  if (!req) return true;
  const city = state.buildings[cityId];
  if (!city) return false;
  const owners = territoryOwners(state, rules);
  return Object.values(state.buildings).some(
    (b) =>
      b.type === req.building &&
      b.owner === city.owner &&
      b.buildProgress >= 1 &&
      buildingLevel(b) >= req.level &&
      owners.get(getHexTileMapKey(b.pos)) === cityId
  );
}

type SpawnRejection = 'unknown' | 'locked' | 'cap' | 'queue' | 'cost' | 'space';

/**
 * Furthest from its building a new unit may appear: late-game cities are
 * ringed by footprints and workers, so 3 often left no free tile.
 */
const SPAWN_RADIUS = 5;

/**
 * Where a new unit goes: a military unit into the city's store for its type
 * (a barracks; a factory for vehicles) when it has room; otherwise (any unit)
 * onto the nearest free open tile around the city's footprint, up to
 * SPAWN_RADIUS away and reachable from it. Undefined when neither is possible.
 */
function spawnSpot(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  city: Building,
  unitType: UnitType
): { store: Building } | { step: Cell } | undefined {
  const who = { owner: city.owner, type: unitType };
  if (isMilitary(ctx.rules, unitType)) {
    const store = storeWithRoom(
      state,
      ctx.rules,
      occ,
      who,
      city.id,
      territoryOwners(state, ctx.rules)
    );
    if (store) return { store };
  }
  const reach = reachOf(state, ctx, { pos: city.pos, owner: city.owner, type: unitType });
  const step = nearestFree(ctx, occ, who, city.pos, 1, SPAWN_RADIUS, reach, city.footprint, [
    'open',
  ]);
  return step ? { step } : undefined;
}

type SpawnCheck = { ok: true } | { ok: false; reason: SpawnRejection };

/**
 * Whether `playerId` may train a `unitType` at city `cityId` now. Shared by
 * the `spawn` command and the Train buttons, so a disabled button and a
 * refused click give the same reason.
 *
 * Checked in order:
 *   - unknown: no such city, not theirs, unfinished, it doesn't train that
 *     type, or the player is missing
 *   - locked: the unit's `requires` building isn't in this city's territory
 *   - cap: they already have unitCap units counting against its cap (`capCount`,
 *     queued ones included)
 *   - queue: a unit that trains (training.ts `trainingBuilding`) and the
 *     city already has MAX_TRAINING_QUEUE queued
 *   - cost: their cities together don't hold `unitCost`
 *   - space: one that appears at once, with no room in the city's barracks
 *     (military) and no free tile within SPAWN_RADIUS of the city (see
 *     `spawnSpot`); a queued unit only needs room once trained, and waits
 *     for it (systems/training.ts)
 */
function canSpawn(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: EntityId,
  unitType: UnitType
): SpawnCheck {
  const { rules } = ctx;
  const city = state.buildings[cityId];
  const unitDef = rules.units[unitType];
  const player = state.players[playerId];
  if (
    !city ||
    city.owner !== playerId ||
    city.buildProgress < 1 ||
    !rules.buildings[city.type]?.spawns?.includes(unitType) ||
    !unitDef ||
    !player ||
    player.eliminated
  ) {
    return { ok: false, reason: 'unknown' };
  }
  if (!unitUnlocked(state, rules, cityId, unitType)) return { ok: false, reason: 'locked' };
  if (capCount(state, rules, playerId, unitType) >= unitCap(state, rules, playerId, unitType)) {
    return { ok: false, reason: 'cap' };
  }
  const timed = trainingBuilding(state, rules, cityId, unitType) !== undefined;
  if (timed && (city.training?.length ?? 0) >= MAX_TRAINING_QUEUE) {
    return { ok: false, reason: 'queue' };
  }
  if (!canAfford(state, playerId, milliCost(unitCost(state, rules, playerId, unitType)))) {
    return { ok: false, reason: 'cost' };
  }
  if (!timed && !spawnSpot(state, ctx, occupancyOf(state, rules), city, unitType)) {
    return { ok: false, reason: 'space' };
  }
  return { ok: true };
}

/**
 * A new unit of `unitType` for `city` at `spot` (from `spawnSpot`): inside
 * the store, or on the free tile; automated when its type starts so, and
 * homed on the city.
 */
function addSpawnedUnit(
  state: SimState,
  ctx: SimContext,
  city: Building,
  unitType: UnitType,
  spot: { store: Building } | { step: Cell },
  id: EntityId
): Unit {
  const unitDef = ctx.rules.units[unitType];
  const at = 'store' in spot ? spot.store.pos : spot.step;
  const unit: Unit = {
    id,
    owner: city.owner,
    type: unitType,
    pos: { q: at.q, r: at.r },
    hp: toMilli(unitDef.hp),
    progress: 0,
    homeCityId: city.id,
    ...('store' in spot ? { inside: spot.store.id } : {}),
    ...(unitDef.automate ? { auto: {} } : {}),
  };
  state.units[id] = unit;
  return unit;
}

/**
 * Trains a unit of the given type at the given city when `canSpawn` allows
 * it, paying for it now: a type with a `trainTime` joins the city's queue
 * at its training building (systems/training.ts), any other appears at
 * once (`spawnSpot`). Mutates state only on success. Returns true if it
 * was queued or spawned.
 */
function trySpawnUnit(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  cityId: EntityId,
  unitType: UnitType
): boolean {
  if (!canSpawn(state, ctx, playerId, cityId, unitType).ok) return false;
  const city = state.buildings[cityId];
  const cost = milliCost(unitCost(state, ctx.rules, playerId, unitType));
  const trainer = trainingBuilding(state, ctx.rules, cityId, unitType);
  if (trainer) {
    spend(state, playerId, cost);
    // The unit's id, minted now: the client knows a trained unit by it.
    const id = mintUnitId(state);
    city.training = [
      ...(city.training ?? []),
      { id, unitType, at: trainer.id, paid: cost, progress: 0 },
    ];
    return true;
  }
  const spot = spawnSpot(state, ctx, occupancyOf(state, ctx.rules), city, unitType);
  if (!spot) return false;
  spend(state, playerId, cost);
  addSpawnedUnit(state, ctx, city, unitType, spot, mintUnitId(state));
  return true;
}

export type { SpawnCheck, SpawnRejection };
export {
  addSpawnedUnit,
  canSpawn,
  capCount,
  capType,
  SPAWN_RADIUS,
  spawnSpot,
  trySpawnUnit,
  unitCap,
  unitCost,
  unitUnlocked,
};
